import {
  QUESTIONS,
  SECTIONS,
  answerLines,
  isMaskedEin,
  normalizeAccountOverlays,
  normalizeJournalOverlays,
  sameAnswer,
  templateLines,
  type Answers,
  type Question,
  type QuestionContext,
  type SectionId,
} from '@lg/shared';

/** Validation of intake answers (spec 8.1). Pure. */

export function isVisible(q: Question, a: Answers, ctx: QuestionContext): boolean {
  return q.visible ? q.visible(a, ctx) : true;
}

export function isRequired(q: Question, a: Answers, ctx: QuestionContext): boolean {
  if (!isVisible(q, a, ctx)) return false;
  return typeof q.required === 'function' ? q.required(a, ctx) : q.required;
}

export function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.values(v as object).every(isEmpty);
  return false;
}

export type AnswerErrors = Record<string, string[]>;

/** Errors per question key, for visible questions only. */
export function validateAnswers(
  a: Answers,
  ctx: QuestionContext,
  sections?: readonly SectionId[],
): AnswerErrors {
  const errors: AnswerErrors = {};
  for (const q of QUESTIONS) {
    if (sections && !sections.includes(q.section)) continue;
    if (!isVisible(q, a, ctx)) continue;
    const value = a[q.key];
    if (isEmpty(value)) {
      if (isRequired(q, a, ctx)) errors[q.key] = ['Required'];
      continue;
    }
    if (q.sensitive && isMaskedEin(value)) continue; // stored securely, validated on entry
    const messages = q.validate?.(value, a, ctx) ?? [];
    if (messages.length) errors[q.key] = messages;
  }
  return errors;
}

export function errorCountBySection(errors: AnswerErrors): Record<SectionId, number> {
  const counts = Object.fromEntries(SECTIONS.map((s) => [s.id, 0])) as Record<SectionId, number>;
  for (const key of Object.keys(errors)) {
    const q = QUESTIONS.find((x) => x.key === key);
    if (q) counts[q.section] += 1;
  }
  return counts;
}

/** Fill template defaults for questions that have no answer yet. */
export function withDefaults(a: Answers, ctx: QuestionContext): Answers {
  const out: Answers = { ...a };
  for (const q of QUESTIONS) {
    if (out[q.key] === undefined && q.defaultValue) out[q.key] = q.defaultValue(ctx);
  }
  return out;
}

/** The answer as compared and shown: overlay rows trimmed, blank rows dropped. */
export function comparableAnswer(q: Question, value: unknown): unknown {
  if (q.type === 'journals') return normalizeJournalOverlays(value);
  if (q.type === 'accountOverrides') return normalizeAccountOverlays(value);
  return value;
}

/**
 * Answers that differ from the template default (spec 8.1 deviation
 * highlighting). Only questions with a template default can deviate; the ledger
 * overlays default to none, so any overlay is a deviation.
 */
export function deviations(a: Answers, ctx: QuestionContext): string[] {
  const out: string[] = [];
  for (const q of QUESTIONS) {
    if (!q.defaultValue || q.key === 'finance.templateId') continue;
    const value = a[q.key];
    if (value === undefined || !isVisible(q, a, ctx)) continue;
    if (!sameAnswer(q.defaultValue(ctx), comparableAnswer(q, value))) out.push(q.key);
  }
  return out;
}

export interface DeviationDetail {
  key: string;
  label: string;
  section: SectionId;
  /** What the template has, in words */
  template: string[];
  /** What this request has instead, one line per value (one per overlay) */
  request: string[];
  /** The requester's reason, when the section asks for one */
  reason: string | null;
}

const REASON_KEY: Partial<Record<SectionId, string>> = {
  finance: 'finance.deviationReason',
  ledger: 'ledger.overlayReason',
};

/** Each deviation side by side with the template, for the review and approval screens. */
export function deviationDetails(a: Answers, ctx: QuestionContext): DeviationDetail[] {
  return deviations(a, ctx).flatMap((key) => {
    const q = QUESTIONS.find((x) => x.key === key);
    if (!q) return [];
    const reasonKey = REASON_KEY[q.section];
    const reason = reasonKey && typeof a[reasonKey] === 'string' ? (a[reasonKey] as string).trim() : '';
    return [
      {
        key,
        label: q.label,
        section: q.section,
        template: templateLines(q, ctx),
        request: answerLines(q, comparableAnswer(q, a[key]), ctx),
        reason: reason || null,
      },
    ];
  });
}
